/**
 * CharacterCard Component
 *
 * Displays a character from the library with their key attributes.
 * Supports different sizes and interaction modes.
 */

import { motion } from 'framer-motion'
import { Heart, Star, MoreVertical, Copy, Trash2, Edit } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { LibraryCharacter } from '@/types/database'

// =============================================================================
// Character Card
// =============================================================================

interface CharacterCardProps {
  character: LibraryCharacter
  onToggleFavorite?: (id: string) => void
  onDelete?: (id: string) => void
  onDuplicate?: (id: string) => void
  /** Whether to show the action menu */
  showActions?: boolean
  className?: string
}

export function CharacterCard({
  character,
  onToggleFavorite,
  onDelete,
  onDuplicate,
  showActions = true,
  className,
}: CharacterCardProps) {
  const [showMenu, setShowMenu] = useState(false)
  const traits = (character.traits as string[]) || []

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={className}
    >
      <Card hoverable className="relative overflow-hidden">
        {/* Favorite indicator */}
        {character.is_favorite && (
          <div className="absolute top-2 right-2 z-10">
            <Star className="w-5 h-5 text-[var(--color-kids-warning)] fill-current" />
          </div>
        )}

        <CardContent className="p-4">
          {/* Header with avatar and name */}
          <div className="flex items-start gap-3 mb-3">
            <div className="w-14 h-14 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center text-3xl">
              {character.avatar_emoji}
            </div>
            <div className="flex-1 min-w-0">
              <Link to={`/characters/${character.id}`}>
                <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] truncate hover:text-[var(--color-kids-primary)] transition-colors">
                  {character.name}
                </h3>
              </Link>
              {traits.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {traits.slice(0, 3).map(trait => (
                    <span
                      key={trait}
                      className="text-xs bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)] px-2 py-0.5 rounded-full"
                    >
                      {trait}
                    </span>
                  ))}
                  {traits.length > 3 && (
                    <span className="text-xs text-[var(--color-kids-text-muted)]">
                      +{traits.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Wants/Fear preview */}
          {(character.wants || character.fear) && (
            <div className="space-y-1 mb-3">
              {character.wants && (
                <p className="text-sm text-[var(--color-kids-text)] line-clamp-1">
                  <span className="text-[var(--color-kids-success)]">Wants:</span>{' '}
                  {character.wants}
                </p>
              )}
              {character.fear && (
                <p className="text-sm text-[var(--color-kids-text)] line-clamp-1">
                  <span className="text-[var(--color-kids-warning)]">Fears:</span>{' '}
                  {character.fear}
                </p>
              )}
            </div>
          )}

          {/* Usage stats */}
          <div className="flex items-center justify-between text-xs text-[var(--color-kids-text-muted)]">
            <span>
              Used in {character.stories_used_in} {character.stories_used_in === 1 ? 'story' : 'stories'}
            </span>

            {/* Actions */}
            {showActions && (
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowMenu(!showMenu)
                  }}
                  className="p-1"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>

                {/* Dropdown menu */}
                {showMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowMenu(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute right-0 bottom-full mb-1 z-20 bg-[var(--color-kids-surface)] rounded-lg shadow-lg border border-[var(--color-kids-surface-alt)] overflow-hidden min-w-[140px]"
                    >
                      <Link
                        to={`/characters/${character.id}`}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-kids-text)] hover:bg-[var(--color-kids-surface-alt)] transition-colors"
                        onClick={() => setShowMenu(false)}
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </Link>
                      {onToggleFavorite && (
                        <button
                          onClick={() => {
                            onToggleFavorite(character.id)
                            setShowMenu(false)
                          }}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-kids-text)] hover:bg-[var(--color-kids-surface-alt)] transition-colors w-full text-left"
                        >
                          <Heart className={cn('w-4 h-4', character.is_favorite && 'fill-current text-[var(--color-kids-warning)]')} />
                          {character.is_favorite ? 'Unfavorite' : 'Favorite'}
                        </button>
                      )}
                      {onDuplicate && (
                        <button
                          onClick={() => {
                            onDuplicate(character.id)
                            setShowMenu(false)
                          }}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-kids-text)] hover:bg-[var(--color-kids-surface-alt)] transition-colors w-full text-left"
                        >
                          <Copy className="w-4 h-4" />
                          Duplicate
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => {
                            onDelete(character.id)
                            setShowMenu(false)
                          }}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-kids-error)] hover:bg-[var(--color-kids-error)]/10 transition-colors w-full text-left"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// =============================================================================
// Compact Character Card (for pickers and small displays)
// =============================================================================

interface CompactCharacterCardProps {
  character: LibraryCharacter
  selected?: boolean
  onClick?: () => void
  className?: string
}

export function CompactCharacterCard({
  character,
  selected = false,
  onClick,
  className,
}: CompactCharacterCardProps) {
  const traits = (character.traits as string[]) || []

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 p-3 rounded-[var(--radius-kid)] w-full text-left transition-all',
        'bg-[var(--color-kids-surface)] border-2',
        selected
          ? 'border-[var(--color-kids-primary)] shadow-[var(--shadow-kid)]'
          : 'border-transparent hover:border-[var(--color-kids-primary)]/30',
        className
      )}
    >
      <div className="w-10 h-10 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center text-xl flex-shrink-0">
        {character.avatar_emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text)] truncate">
            {character.name}
          </h4>
          {character.is_favorite && (
            <Star className="w-3 h-3 text-[var(--color-kids-warning)] fill-current flex-shrink-0" />
          )}
        </div>
        {traits.length > 0 && (
          <p className="text-xs text-[var(--color-kids-text-muted)] truncate">
            {traits.slice(0, 2).join(', ')}
            {traits.length > 2 && '...'}
          </p>
        )}
      </div>
      {selected && (
        <div className="w-5 h-5 rounded-full bg-[var(--color-kids-primary)] flex items-center justify-center flex-shrink-0">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </motion.button>
  )
}

// =============================================================================
// Character Template Card (for starter templates in wizard)
// =============================================================================

interface TemplateCardProps {
  template: {
    name: string
    avatar_emoji: string
    traits: string[]
    wants: string
    fear: string
    description: string
  }
  onClick?: () => void
  className?: string
}

export function TemplateCard({
  template,
  onClick,
  className,
}: TemplateCardProps) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        'p-4 rounded-[var(--radius-kid)] w-full text-left transition-all',
        'bg-[var(--color-kids-surface)] border-2 border-transparent',
        'hover:border-[var(--color-kids-primary)]/30 hover:shadow-[var(--shadow-kid)]',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center text-2xl flex-shrink-0">
          {template.avatar_emoji}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-display font-bold text-base text-[var(--color-kids-text)]">
            {template.name}
          </h4>
          <p className="text-sm text-[var(--color-kids-text-muted)] mt-1">
            {template.description}
          </p>
          <div className="flex flex-wrap gap-1 mt-2">
            {template.traits.map(trait => (
              <span
                key={trait}
                className="text-xs bg-[var(--color-kids-primary)]/10 text-[var(--color-kids-primary)] px-2 py-0.5 rounded-full"
              >
                {trait}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.button>
  )
}

export default CharacterCard
